import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, Tooltip, notification } from "antd";
import Swal from "sweetalert2";
import HashLoader from "react-spinners/ClipLoader";
import fetchSiteList from "../../../api/sites/fetchSiteList";
import DeleteIcon from "../../../assets/delete.svg";
import EditIcon from "../../../assets/edit.svg";

const emptyForm = { name: "", site_id: "", pdf: null };

const ManualList = () => {
  const url = process.env.REACT_APP_APP_BACK_URL;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const isAuthenticated = useSelector((state) => state.user);
  const isSuperAdmin =
    String(isAuthenticated?.role || "").toLowerCase() === "superadmin";
  const [sites, setSites] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [fileKey, setFileKey] = useState(0);

  const authHeaders = () => {
    const headers = new Headers();
    headers.append("auth-token", localStorage.getItem("token"));
    return headers;
  };

  const defaultSiteId = (list) => (list.length === 1 ? list[0]._id : "");

  const loadRows = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${url}/api/manual/list`, {
        headers: authHeaders(),
      });
      const json = await response.json();
      setRows(Array.isArray(json.data) ? json.data : []);
    } catch (error) {
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const siteData = await fetchSiteList();
      const list = siteData?.site_data || [];
      setSites(list);
      setForm((prev) => ({ ...prev, site_id: prev.site_id || defaultSiteId(list) }));
    })();
    loadRows();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, site_id: defaultSiteId(sites) });
    setFileKey((key) => key + 1);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ name: item.name, site_id: item.site_id, pdf: null });
    setFileKey((key) => key + 1);
    setOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setOpen(false);
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editing && !form.pdf) {
      notification.error({ message: "Failed", description: "Choose a PDF", duration: 3 });
      return;
    }
    const body = new FormData();
    body.append("name", form.name);
    body.append("site_id", form.site_id);
    if (form.pdf) body.append("pdf", form.pdf);
    setIsSaving(true);
    try {
      const response = await fetch(
        editing ? `${url}/api/manual/update/${editing._id}` : `${url}/api/manual/add`,
        {
          method: editing ? "PUT" : "POST",
          headers: authHeaders(),
          body,
        }
      );
      const json = await response.json();
      if (!json.success) {
        notification.error({
          message: "Failed",
          description: json.error || "Could not save the manual",
          duration: 3,
        });
        return;
      }
      notification.success({
        message: "Success",
        description: editing ? "Manual updated" : "Manual uploaded",
        duration: 3,
      });
      setOpen(false);
      setEditing(null);
      loadRows();
    } catch (error) {
      notification.error({
        message: "Failed",
        description: "Could not save the manual",
        duration: 3,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This manual will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    const response = await fetch(`${url}/api/manual/delete/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    const json = await response.json();
    if (json.success) {
      setRows((prev) => prev.filter((item) => item._id !== id));
      Swal.fire("Deleted!", "Manual deleted successfully!", "success");
    } else {
      Swal.fire("Error", json.error || "Could not delete", "error");
    }
  };

  const handlePrint = (item) => {
    const pdfUrl = `${baseUrl}${item.file}`;
    const frame = document.createElement("iframe");
    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    frame.src = pdfUrl;
    frame.onload = () => {
      try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
      } catch (error) {
        window.open(pdfUrl, "_blank");
      }
    };
    document.body.appendChild(frame);
  };

  const pdfLabel = (item) => {
    const name = item.original_name || `${item.name}.pdf`;
    return name.toLowerCase().endsWith(".pdf") ? name : `${name}.pdf`;
  };

  return (
    <div className="general-dashboard">
      <div className="container-fluid">
        <div className="dashboard-top-row">
          <div className="row">
            <div className="col-lg-6 my-auto">
              <div className="user-wrap">
                <h3>Manual</h3>
                <p>PDF manuals for each site</p>
              </div>
            </div>
            <div className="col-lg-6 text-end">
              {isSuperAdmin && (
                <button type="button" className="btn btn-prim w-auto" onClick={openAdd}>
                  Add Manual
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="table-wrap">
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Site</th>
                  <th>PDF</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={4} className="text-center">
                      <HashLoader color="#0b0d41" loading size={40} />
                    </td>
                  </tr>
                )}
                {!isLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center">
                      No manuals yet
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  rows.map((item) => (
                    <tr key={item._id}>
                      <td>{item.name}</td>
                      <td>{item.site || "—"}</td>
                      <td>{pdfLabel(item)}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {isSuperAdmin && (
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              onClick={() => openEdit(item)}
                            >
                              <Tooltip title="Edit">
                                <img src={EditIcon} alt="edit" />
                              </Tooltip>
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-link p-0 d-flex align-items-center gap-1"
                            onClick={() => handlePrint(item)}
                          >
                            <Tooltip title="Print">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M7 8V3h10v5" stroke="#012269" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M7 17H5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" stroke="#012269" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M7 14h10v7H7v-7Z" stroke="#012269" strokeWidth="1.6" strokeLinejoin="round" />
                              </svg>
                            </Tooltip>
                          </button>
                          {isSuperAdmin && (
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              onClick={() => handleDelete(item._id)}
                            >
                              <Tooltip title="Delete">
                                <img src={DeleteIcon} alt="delete" />
                              </Tooltip>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Modal
        title={editing ? "Edit Manual" : "Add Manual"}
        open={open}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
        centered
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label" htmlFor="manual-name">
              Name
            </label>
            <input
              id="manual-name"
              type="text"
              className="form-control"
              placeholder="Manual name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="manual-site">
              Site
            </label>
            <select
              id="manual-site"
              className="form-control"
              value={form.site_id}
              onChange={(e) => setForm({ ...form, site_id: e.target.value })}
              required
              disabled={!isSuperAdmin}
            >
              <option value="">Choose Site</option>
              {sites.map((site) => (
                <option key={site._id} value={site._id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="manual-pdf">
              PDF
            </label>
            <input
              id="manual-pdf"
              key={fileKey}
              type="file"
              className="form-control"
              accept="application/pdf,.pdf"
              onChange={(e) => setForm({ ...form, pdf: e.target.files?.[0] || null })}
              required={!editing}
            />
            {editing && (
              <p className="mb-0 mt-2" style={{ color: "#888", fontSize: 12 }}>
                Current file: {pdfLabel(editing)}. Choose a new PDF only if you want to replace it.
              </p>
            )}
          </div>
          <div className="text-end">
            <button type="button" className="btn btn-light me-2" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-prim w-auto" disabled={isSaving}>
              {isSaving ? "Saving..." : editing ? "Update" : "Upload"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ManualList;
